import { Box, useTheme } from "@mui/material";
import React, { ReactNode, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ModalContext } from "../../Modals";
import ModalHttpUpload, { ModalHttpUploadProps } from "../../Modals/ModalHttpUpload/modal-http-upload";
import ModalAsperaUpload, { ModalAsperaUploadProps } from "../../Modals/ModalAsperaUpload/modal-aspera-upload";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { getDroppedOrSelectedFiles } from "../../../Utils/Helpers/fileSelector";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { cacheService } from "../../../Core/Api/services";

export type DropFilesWrapperProps = {
  userCanUploadFiles: boolean;
  currentAsset: any;
  children: ReactNode;
};

const DropFilesWrapper: React.FC<DropFilesWrapperProps> = ({ userCanUploadFiles, currentAsset, children }) => {
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const theme = useTheme();
  const isCloud = cacheService.GetPlatformSettings()?.cloud ?? false;

  const onDrop = useCallback(
    (e) => {
      let shouldUseAspera = false;
      if (e.length > GlobalConst.MAX_FILES_COUNT_HTTP) shouldUseAspera = true;
      for (let i = 0; i < e.length; i++) {
        if (e[i].size > GlobalConst.MAX_FILE_SIZE_HTTP_UPLOAD) shouldUseAspera = true;
      }
      setDropzoneIsDragOver(false);
      if (!shouldUseAspera) {
        openModal &&
          currentAsset &&
          openModal<ModalHttpUploadProps>(ModalHttpUpload, {
            assetParentName: currentAsset.parentObject?.name || currentAsset.parentTitleName || "",
            assetId: currentAsset.id,
            assetName: currentAsset.name,
            files: e.reverse(),
            closeModal: () => {},
          });
      } else {
        openModal &&
          currentAsset &&
          openModal<ModalAsperaUploadProps>(ModalAsperaUpload, {
            assetParentName: currentAsset.parentObject?.name || currentAsset.parentTitleName || "",
            assetId: currentAsset.id,
            assetName: currentAsset.name,
            assetAnnexFormatIds: [],
            assetType: currentAsset.type || ObjectType.Folder,
            assetClassification: ObjectClass.Drive,
            assetIsHdr: false,
            assetProviderId: 0,
            showWarning: true,
            useToken: isCloud,
            closeModal: () => {},
          });
      }
    },
    [currentAsset, openModal]
  );
  const [dropzoneIsDragOver, setDropzoneIsDragOver] = useState(false);

  const { getRootProps } = useDropzone({
    multiple: true,
    disabled: !userCanUploadFiles,
    onDragEnter: () => setDropzoneIsDragOver(true),
    onDragLeave: () => setDropzoneIsDragOver(false),
    onDragOver: () => setDropzoneIsDragOver(true),

    getFilesFromEvent: (event) => getFilesFromEvent(event),
  });

  const getFilesFromEvent = useCallback(
    (e: any) => {
      if (e.type !== "drop" && e[0] && e[0].kind === "file")
        return new Promise<any>((resolve) =>
          Promise.all(e.map((x) => new Promise((resolve) => resolve(x.getFile())))).then((result) => {
            onDrop(result);
            resolve(result);
          })
        );
      if (e.type !== "drop") return new Promise<any>((resolve) => {});

      return new Promise<any>((resolve) => {
        getDroppedOrSelectedFiles(e).then((chosenFiles) => {
          onDrop(chosenFiles);
          resolve(chosenFiles.map((f: any) => f.fileObject));
        });
      });
    },
    [currentAsset]
  );

  return (
    <Box
      sx={{
        borderRadius: 3,

        border: `2px dotted ${
          userCanUploadFiles && dropzoneIsDragOver ? theme.palette.primary.mainHover : theme.palette.background.default
        }`,
        boxSizing: "border-box",
        width: "100%",
        height: "100%",
        zIndex: 1100,
      }}
    >
      {userCanUploadFiles ? (
        <Box
          {...getRootProps()}
          onClick={(e) => {
            e.stopPropagation();
          }}
          sx={{
            width: "100%",
            height: "100%",
          }}
        >
          {children}
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

export default DropFilesWrapper;
